import {recordedFixture} from "../components/football-app/recordedFixture";
import {canViewFootballAnalytics} from "../components/football-intelligence/analyticsAccess";
// src/pages/FootballMatchDetail.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link, Navigate } from "react-router-dom";
import { api, API_BASE, fetchFixtureEdges } from "../api";
import { useMatchIntelligence } from "../components/football-intelligence/MatchIntelligence";
import { useAuth } from "../components/AuthGate";
import MatchHeaderCompact from "../components/football-app/MatchHeaderCompact";
import ScrollTabs, { Segmented } from "../components/football-app/ScrollTabs";
import MatchLineups from "../components/football-app/MatchLineups";
import RecordedMatchFallback from "../components/football-app/RecordedMatchFallback";
import MatchAnalysis from "../components/football-app/MatchAnalysis";
import { FullStats, KeyEvents, OverviewStats } from "../components/football-app/MatchFacts";
import { goalLines } from "../components/football-app/lineupModel";
import { SECTIONS, sectionOf, subsFor } from "../components/football-app/matchSections";
import "../components/football-app/football-app.css";
import FormBreakdown from "../components/FormBreakdown";
import MatchInsights from "../components/MatchInsights";
import H2HSection from "../components/H2HSection";
import TeamContextPanel from "../components/TeamContextPanel";
import EventsSection from "../components/EventsSection";
import PredictionsSection from "../components/PredictionsSection";
import PlayersSection from "../components/PlayersSection";
import InjuriesSection from "../components/InjuriesSection";
import PlayerPropsSection from "../components/PlayerPropsSection";
import Poll from "../components/Poll";
import LeagueFixtures from "../components/LeagueFixtures";
import FixtureAccessPill from "../components/FixtureAccessPill";
import OddsTable from "../components/OddsTable";
import SportsNewsSection from "../components/SportsNewsSection";
import FixtureSocialPosts from "../components/FixtureSocialPosts";
import HighlightVideosSection from "../components/HighlightVideosSection";
import MediaWatchSection from "../components/MediaWatchSection";
import SocialEngagement from "../components/SocialEngagement";
import EdgeExplanationCard from "../components/EdgeExplanationCard";
import { slugifyTeamName } from "../utils/slugify";
import { formatOddsBoth } from "../utils/oddsFormat";
import { getLeagueCodeFromAny, getLeagueNameFromAny } from "../utils/leagueRouting";
import { hasCupContext } from "../utils/insightContext";
import { formatMarketName } from "../utils/formatMarketName";
import { isAllowedPublicEdge } from "../utils/publicEdge";
import {
  WORLD_CUP_CODE,
  WORLD_CUP_SEASON,
  findWorldCupFixtureGroup,
  isWorldCupCompetition,
  normalizeWorldCupGroups,
  normalizeWorldCupTeamName,
} from "../utils/worldCup";

const LIVE_REFRESH_DEBUG = process.env.NODE_ENV !== "production";
const LEAGUE_TABLE_DEBUG = process.env.NODE_ENV !== "production";

/* ── colour palette ────────────────────────────────────────── */
const C = {
  pageBg:     "#f0fdf4",
  cardBg:     "#ffffff",
  pitchLine:  "#16a34a",
  pitchStripe:"#22c55e",
  goalBox:    "#15803d",
  accentDark: "#166534",
  accentLight:"#bbf7d0",
  textPri:    "#000000",          // Pure black for all text
  textSub:    "#000000",          // Pure black for all text
  textMut:    "#000000",          // Pure black for all text
  green:      "#16a34a",
  greenLt:    "rgba(22,163,74,0.10)",
  red:        "#dc2626",
  redLt:      "rgba(220,38,38,0.10)",
};

const HERO = {
  panel: "linear-gradient(145deg, #07140f 0%, #0b2118 52%, #07110d 100%)",
  card: "rgba(255,255,255,0.055)",
  border: "rgba(134,239,172,0.18)",
  text: "#f0fdf4",
  sub: "#9fb7a8",
  green: "#31d48b",
  amber: "#fbbf24",
};

const safeNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formGoals = (match) => {
  const directFor = Number(match?.goals_for);
  const directAgainst = Number(match?.goals_against);
  if (Number.isFinite(directFor) && Number.isFinite(directAgainst)) {
    return { gf: directFor, ga: directAgainst };
  }

  const home = Number(match?.home_goals);
  const away = Number(match?.away_goals);
  if (Number.isFinite(home) && Number.isFinite(away)) {
    return match?.is_home === false ? { gf: away, ga: home } : { gf: home, ga: away };
  }

  const score = String(match?.score || "").match(/(\d+)\s*[-:]\s*(\d+)/);
  return score ? { gf: Number(score[1]), ga: Number(score[2]) } : { gf: null, ga: null };
};

const summarizeForm = (teamForm) => {
  const recent = Array.isArray(teamForm?.recent) ? teamForm.recent : [];
  const derived = recent.reduce(
    (summary, match) => {
      const { gf, ga } = formGoals(match);
      if (!Number.isFinite(gf) || !Number.isFinite(ga)) return summary;
      summary.played += 1;
      summary.gf += gf;
      summary.ga += ga;
      if (gf > ga) summary.wins += 1;
      else if (gf === ga) summary.draws += 1;
      else summary.losses += 1;
      summary.results.push(gf > ga ? "W" : gf === ga ? "D" : "L");
      return summary;
    },
    { played: 0, wins: 0, draws: 0, losses: 0, gf: 0, ga: 0, results: [] }
  );

  if (derived.played) return derived;
  const fallback = teamForm?.summary || teamForm || {};
  const played = safeNumber(fallback.played);
  return {
    played,
    wins: safeNumber(fallback.wins),
    draws: safeNumber(fallback.draws),
    losses: safeNumber(fallback.losses),
    gf: safeNumber(fallback.goals_for),
    ga: safeNumber(fallback.goals_against),
    results: [],
  };
};

function ResultStrip({ results }) {
  if (!results.length) return <span style={{ color: HERO.sub, fontSize: 12 }}>Form pending</span>;
  const chronologicalResults = [...results.slice(0, 5)].reverse();
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {chronologicalResults.map((result, index) => {
        const tone =
          result === "W"
            ? { bg: "rgba(49,212,139,.18)", border: "rgba(49,212,139,.4)", text: "#86efac" }
            : result === "L"
              ? { bg: "rgba(248,113,113,.14)", border: "rgba(248,113,113,.34)", text: "#fca5a5" }
              : { bg: "rgba(251,191,36,.13)", border: "rgba(251,191,36,.3)", text: "#fde68a" };
        return (
          <span
            key={`${result}-${index}`}
            style={{
              width: 27,
              height: 27,
              borderRadius: 8,
              display: "grid",
              placeItems: "center",
              background: tone.bg,
              border: `1px solid ${tone.border}`,
              color: tone.text,
              fontSize: 11,
              fontWeight: 900,
            }}
          >
            {result}
          </span>
        );
      })}
    </div>
  );
}

function MatchPulse({ formData, homeName, awayName, n = 5, onOpenForm }) {
  const home = summarizeForm(formData?.home);
  const away = summarizeForm(formData?.away);
  const metricRows = [
    {
      label: "Win rate",
      home: home.played ? (home.wins / home.played) * 100 : 0,
      away: away.played ? (away.wins / away.played) * 100 : 0,
      format: (value) => `${value.toFixed(0)}%`,
    },
    {
      label: "Goals / game",
      home: home.played ? home.gf / home.played : 0,
      away: away.played ? away.gf / away.played : 0,
      format: (value) => value.toFixed(1),
    },
    {
      label: "Goals allowed",
      home: home.played ? home.ga / home.played : 0,
      away: away.played ? away.ga / away.played : 0,
      format: (value) => value.toFixed(1),
      lowerIsBetter: true,
    },
  ];
  const recentRows = (teamForm) => (Array.isArray(teamForm?.recent) ? teamForm.recent.slice(0, n) : []);
  const recentList = (teamForm, teamName) => {
    const rows = recentRows(teamForm);
    if (!rows.length) return <span style={{ color: HERO.sub, fontSize: 12 }}>Recent scores pending</span>;
    return (
      <div style={{ display: "grid", gap: 7 }}>
        {rows.map((match, index) => {
          const { gf, ga } = formGoals(match);
          const result = Number.isFinite(gf) && Number.isFinite(ga) ? (gf > ga ? "W" : gf === ga ? "D" : "L") : "—";
          const tone = result === "W" ? "#86efac" : result === "L" ? "#fca5a5" : "#fde68a";
          const date = match?.date || match?.kickoff_utc
            ? new Date(match.date || match.kickoff_utc).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })
            : "—";
          return (
            <div
              key={match?.fixture_id || `${match?.opponent}-${index}`}
              style={{
                display: "grid",
                gridTemplateColumns: "52px minmax(0, 1fr) auto auto",
                gap: 9,
                alignItems: "center",
                padding: "8px 10px",
                borderRadius: 10,
                background: HERO.card,
                border: `1px solid ${HERO.border}`,
                fontSize: 12,
              }}
            >
              <span style={{ color: HERO.sub }}>{date}</span>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {match?.is_home === false ? "@" : "vs"} {match?.opponent || "Opponent"}
              </span>
              <strong>{Number.isFinite(gf) && Number.isFinite(ga) ? `${gf}-${ga}` : match?.score || "—"}</strong>
              <strong style={{ color: tone }}>{result}</strong>
            </div>
          );
        })}
        <span style={{ color: HERO.sub, fontSize: 11 }}>{teamName} score shown first · all competitions</span>
      </div>
    );
  };

  return (
    <section
      style={{
        borderRadius: 20,
        padding: 22,
        marginBottom: 20,
        color: HERO.text,
        background: HERO.panel,
        border: `1px solid ${HERO.border}`,
        boxShadow: "0 18px 42px rgba(5,46,22,.14)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 18, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div>
          <div style={{ color: HERO.green, fontSize: 12, fontWeight: 900, letterSpacing: 1.6, textTransform: "uppercase" }}>
            Match Pulse
          </div>
          <h2 style={{ margin: "5px 0 4px", fontSize: 24, color: HERO.text }}>Recent form at a glance</h2>
          <p style={{ margin: 0, color: HERO.sub, fontSize: 13 }}>Last {n} matches · team scoring and defensive rhythm</p>
        </div>
        <button
          onClick={onOpenForm}
          style={{
            border: `1px solid ${HERO.border}`,
            background: HERO.card,
            color: HERO.text,
            borderRadius: 999,
            padding: "8px 13px",
            fontWeight: 800,
            cursor: "pointer",
          }}
        >
          Full form →
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            typeof window !== "undefined" && window.innerWidth < 820
              ? "1fr"
              : "minmax(150px,1fr) minmax(260px,1.6fr) minmax(150px,1fr)",
          gap: 18,
          marginTop: 22,
          alignItems: "center",
        }}
      >
        <div>
          <strong style={{ display: "block", fontSize: 18, marginBottom: 9 }}>{homeName}</strong>
          <ResultStrip results={home.results} />
          <span style={{ display: "block", marginTop: 9, color: HERO.sub, fontSize: 12 }}>
            {home.wins}W · {home.draws}D · {home.losses}L
          </span>
        </div>
        <div style={{ display: "grid", gap: 13 }}>
          {metricRows.map((metric) => {
            const maximum = Math.max(metric.home, metric.away, 1);
            const homeWidth = `${Math.max(5, (metric.home / maximum) * 100)}%`;
            const awayWidth = `${Math.max(5, (metric.away / maximum) * 100)}%`;
            return (
              <div key={metric.label}>
                <div style={{ display: "grid", gridTemplateColumns: "48px 1fr 96px 1fr 48px", gap: 8, alignItems: "center" }}>
                  <strong style={{ textAlign: "right", fontSize: 13 }}>{metric.format(metric.home)}</strong>
                  <div style={{ height: 7, borderRadius: 99, background: "rgba(255,255,255,.07)", overflow: "hidden", display: "flex", justifyContent: "flex-end" }}>
                    <div style={{ width: homeWidth, borderRadius: 99, background: metric.lowerIsBetter ? "#60a5fa" : HERO.green }} />
                  </div>
                  <span style={{ textAlign: "center", color: HERO.sub, fontSize: 11 }}>{metric.label}</span>
                  <div style={{ height: 7, borderRadius: 99, background: "rgba(255,255,255,.07)", overflow: "hidden" }}>
                    <div style={{ width: awayWidth, height: "100%", borderRadius: 99, background: metric.lowerIsBetter ? "#60a5fa" : HERO.amber }} />
                  </div>
                  <strong style={{ fontSize: 13 }}>{metric.format(metric.away)}</strong>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ textAlign: typeof window !== "undefined" && window.innerWidth < 820 ? "left" : "right" }}>
          <strong style={{ display: "block", fontSize: 18, marginBottom: 9 }}>{awayName}</strong>
          <div style={{ display: "flex", justifyContent: typeof window !== "undefined" && window.innerWidth < 820 ? "flex-start" : "flex-end" }}><ResultStrip results={away.results} /></div>
          <span style={{ display: "block", marginTop: 9, color: HERO.sub, fontSize: 12 }}>
            {away.wins}W · {away.draws}D · {away.losses}L
          </span>
        </div>
      </div>
      <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
        <div>
          <div style={{ marginBottom: 8, color: HERO.sub, fontSize: 11, fontWeight: 900, letterSpacing: 0.8, textTransform: "uppercase" }}>
            {homeName} recent scores
          </div>
          {recentList(formData?.home, homeName)}
        </div>
        <div>
          <div style={{ marginBottom: 8, color: HERO.sub, fontSize: 11, fontWeight: 900, letterSpacing: 0.8, textTransform: "uppercase" }}>
            {awayName} recent scores
          </div>
          {recentList(formData?.away, awayName)}
        </div>
      </div>
    </section>
  );
}

function normalizeExplainMarket(raw) {
  const m = String(raw).toUpperCase().trim();
  if (m === "1" || m === "HOME" || m === "HOME_WIN") return "HOME_WIN";
  if (m === "X" || m === "DRAW") return "DRAW";
  if (m === "2" || m === "AWAY" || m === "AWAY_WIN") return "AWAY_WIN";
  if (m.startsWith("O")) return "O2.5";
  if (m.startsWith("U")) return "U2.5";
  if (m.includes("BTTS")) {
    if (m.includes("YES") || m.includes("Y")) return "BTTS_YES";
    if (m.includes("NO") || m.includes("N")) return "BTTS_NO";
  }
  return m;
}

function getExplainNotes(explanation, edge) {
  const rowWhy = getRowWhy(edge);
  if (rowWhy) return [cleanMarketKeysInText(rowWhy)];
  if (isStatPropMarket(edge?.market)) return [buildStatPropFallbackWhy(edge)];
  if (Array.isArray(explanation?.notes) && explanation.notes.length) {
    return explanation.notes.map((line) => cleanMarketKeysInText(line));
  }
  return [];
}

function humanizeExplainText(input) {
  return cleanMarketKeysInText(input)
    .replace(/\b1H\b/gi, "First Half")
    .replace(/\b2H\b/gi, "Second Half")
    .replace(/\bHT\b/g, "Half-time");
}

function isStatPropMarket(market) {
  return /^(?:(HOME|AWAY)_)?(CORNERS|CARDS|SOG)_[OU]\d+(\.\d+)?$/i.test(String(market || "").trim());
}

function getRowWhy(edge) {
  return firstDefined(edge?.why, edge?.explanation, edge?.reason, edge?.why_text, "");
}

function cleanMarketKeysInText(input) {
  return String(input ?? "").replace(
    /\b(?:(?:HOME|AWAY)_)?(?:CORNERS|CARDS|SOG)_[OU]\d+(?:\.\d+)?\b/g,
    (match) => formatMarketName(match)
  );
}

function buildStatPropFallbackWhy(edge) {
  const marketLabel = formatMarketName(edge?.market);
  const modelProb = Number(edge?.prob);
  const price = Number(edge?.price);
  const edgeValue = Number(edge?.edge);
  const bookmaker = edge?.bookmaker || "the market";
  const modelText = Number.isFinite(modelProb) ? `${(modelProb * 100).toFixed(1)}%` : null;
  const impliedText = Number.isFinite(price) && price > 0 ? `${((1 / price) * 100).toFixed(1)}%` : null;
  const edgeText = Number.isFinite(edgeValue) ? `${(edgeValue * 100).toFixed(1)}%` : null;

  if (modelText && impliedText && edgeText) {
    return `Model predicts a ${modelText} chance for ${marketLabel}, but ${bookmaker} odds imply ${impliedText}, creating a ${edgeText} edge.`;
  }
  if (modelText && edgeText) {
    return `Model predicts a ${modelText} chance for ${marketLabel}, creating a ${edgeText} edge.`;
  }
  if (modelText) {
    return `Model predicts a ${modelText} chance for ${marketLabel}.`;
  }
  return `${marketLabel} is priced with a model edge on this row.`;
}

const FINISHED_STATUSES = ["FT", "AET", "PEN", "FINISHED", "FULLTIME"];
const LIVE_STATUSES = ["1H", "2H", "HT", "ET", "LIVE", "INPLAY", "INT", "BREAK"];

function normalizeEventRows(rawEvents) {
  return (Array.isArray(rawEvents) ? rawEvents : []).map((e, idx) => ({
    id: e?.id ?? `${e?.type || "ev"}-${e?.elapsed ?? "x"}-${idx}`,
    elapsed: e?.elapsed ?? e?.time?.elapsed ?? null,
    extra: e?.extra ?? e?.time?.extra ?? null,
    team_id: e?.team_id ?? e?.team?.id ?? null,
    team_name: e?.team_name ?? e?.team?.name ?? "",
    type: e?.type ?? "",
    detail: e?.detail ?? "",
    comments: e?.comments ?? "",
    player_name: e?.player_name ?? e?.player?.name ?? "",
    assist_name: e?.assist_name ?? e?.assist?.name ?? "",
  }));
}

function eventTypeKey(v) {
  return String(v || "").trim().toLowerCase();
}

function isGoalEvent(event) {
  return eventTypeKey(event?.type) === "goal";
}

function isCardEvent(event) {
  return eventTypeKey(event?.type) === "card";
}

function isSubstitutionEvent(event) {
  const type = eventTypeKey(event?.type);
  const detail = eventTypeKey(event?.detail);
  return type === "subst" || type === "substitution" || detail.includes("substitution");
}

function isTimelineEvent(event) {
  return isGoalEvent(event) || isCardEvent(event) || isSubstitutionEvent(event);
}

function isRedCardEvent(event) {
  const detail = eventTypeKey(event?.detail);
  return detail.includes("red") || detail.includes("second yellow") || detail.includes("2nd yellow");
}

function formatEventMinute(event) {
  const elapsed = Number(event?.elapsed);
  const extra = Number(event?.extra);
  if (Number.isFinite(elapsed) && Number.isFinite(extra) && extra > 0) return `${elapsed}+${extra}'`;
  if (Number.isFinite(elapsed)) return `${elapsed}'`;
  return "—";
}

function sortEventsAsc(events) {
  return [...events].sort((a, b) => {
    const ae = Number(a?.elapsed);
    const be = Number(b?.elapsed);
    const ax = Number(a?.extra);
    const bx = Number(b?.extra);
    const aElapsed = Number.isFinite(ae) ? ae : -1;
    const bElapsed = Number.isFinite(be) ? be : -1;
    if (aElapsed !== bElapsed) return aElapsed - bElapsed;
    return (Number.isFinite(ax) ? ax : 0) - (Number.isFinite(bx) ? bx : 0);
  });
}

function timelineStatusLabel(statusMeta) {
  if (statusMeta?.isFinished) return "FT";
  if (statusMeta?.isLive) {
    if (String(statusMeta.status) === "HT") return "HT";
    return Number.isFinite(statusMeta.elapsed) ? `Live ${statusMeta.elapsed}'` : "Live";
  }
  return "Pre-match";
}

function TimelineEventIcon({ event }) {
  const redCard = isRedCardEvent(event);
  if (isGoalEvent(event)) return <span style={{ color: C.pitchLine, fontWeight: 900 }}>Goal</span>;
  if (isSubstitutionEvent(event)) return <span style={{ color: "#2563eb", fontWeight: 900 }}>Sub</span>;
  if (isCardEvent(event)) {
    return (
      <span
        style={{
          display: "inline-block",
          width: 12,
          height: 16,
          borderRadius: 3,
          background: redCard ? "#dc2626" : "#facc15",
          border: redCard ? "1px solid #991b1b" : "1px solid #ca8a04",
          boxShadow: "0 1px 2px rgba(0,0,0,0.18)",
        }}
        aria-label={redCard ? "Red card" : "Yellow card"}
      />
    );
  }
  return null;
}

function TimelineEventBody({ event }) {
  if (isGoalEvent(event)) {
    return (
      <>
        <div style={{ fontWeight: 800, color: "#000" }}>
          {event.player_name || "Goal"}
        </div>
        {event.assist_name ? (
          <div style={{ marginTop: 3, fontSize: 12, color: C.textSub }}>
            Assist: {event.assist_name}
          </div>
        ) : null}
      </>
    );
  }

  if (isSubstitutionEvent(event)) {
    const off = event.assist_name || event.comments || "";
    const on = event.player_name || "";
    return (
      <>
        <div style={{ fontWeight: 800, color: "#000" }}>Substitution</div>
        <div style={{ marginTop: 3, fontSize: 13, color: C.textSub }}>
          {off && on ? `${off} -> ${on}` : on || off || event.detail || "Player change"}
        </div>
      </>
    );
  }

  if (isCardEvent(event)) {
    return (
      <>
        <div style={{ fontWeight: 800, color: isRedCardEvent(event) ? "#b91c1c" : "#854d0e" }}>
          {event.detail || (isRedCardEvent(event) ? "Red Card" : "Yellow Card")}
        </div>
        <div style={{ marginTop: 3, fontSize: 13, color: C.textSub }}>
          {event.player_name || event.comments || "Card shown"}
        </div>
      </>
    );
  }

  return <div style={{ color: "#000" }}>{event.detail || event.type || "Event"}</div>;
}

function MatchTimeline({ events, statusMeta, homeTeamName, awayTeamName }) {
  const rows = sortEventsAsc((events || []).filter(isTimelineEvent));
  const status = timelineStatusLabel(statusMeta);
  const emptyMessage = statusMeta?.isFinished
    ? "The provider did not return key events for this fixture."
    : "Goals, cards and substitutions will appear here once available.";

  return (
    <div
      style={{
        background: C.cardBg,
        borderRadius: 12,
        padding: 20,
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, color: "#000", fontSize: 20 }}>Timeline</h3>
          <div style={{ marginTop: 4, color: C.textSub, fontSize: 13 }}>
            {homeTeamName} vs {awayTeamName}
          </div>
        </div>
        <span
          style={{
            border: `1px solid ${statusMeta?.isLive ? "rgba(22,163,74,.45)" : "rgba(0,0,0,.1)"}`,
            background: statusMeta?.isLive ? "rgba(22,163,74,.12)" : "rgba(0,0,0,.04)",
            color: "#000",
            borderRadius: 8,
            padding: "6px 10px",
            fontSize: 13,
            fontWeight: 800,
          }}
        >
          {status}
        </span>
      </div>

      {!rows.length ? (
        <div
          style={{
            border: "1px dashed rgba(0,0,0,0.16)",
            borderRadius: 8,
            padding: 16,
            color: C.textSub,
            background: "rgba(0,0,0,0.025)",
          }}
        >
          {emptyMessage}
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {rows.map((event, index) => (
            <div
              key={event.id || `${event.type}-${event.elapsed}-${index}`}
              style={{
                display: "grid",
                gridTemplateColumns: "58px 1px minmax(0, 1fr)",
                gap: 12,
                alignItems: "stretch",
              }}
            >
              <div style={{ color: "#000", fontWeight: 900, textAlign: "right", paddingTop: 10 }}>
                {formatEventMinute(event)}
              </div>
              <div style={{ background: index === rows.length - 1 ? "transparent" : C.accentLight, position: "relative" }}>
                <span
                  style={{
                    position: "absolute",
                    top: 12,
                    left: -5,
                    width: 11,
                    height: 11,
                    borderRadius: 999,
                    background: C.pitchLine,
                    border: "2px solid #fff",
                  }}
                />
              </div>
              <div
                style={{
                  border: "1px solid rgba(0,0,0,0.08)",
                  borderRadius: 8,
                  padding: "10px 12px",
                  background: "#fff",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
                  <div style={{ minWidth: 0 }}>
                    <TimelineEventBody event={event} />
                    <div style={{ marginTop: 5, fontSize: 12, color: C.textSub }}>
                      {event.team_name || ""}
                    </div>
                  </div>
                  <TimelineEventIcon event={event} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function getFixtureStatusMeta(fixture) {
  const nestedFixture = typeof fixture?.fixture === "object" && fixture?.fixture ? fixture.fixture : null;
  const nestedStatus = typeof nestedFixture?.status === "object" && nestedFixture?.status ? nestedFixture.status : null;
  const rawStatus = firstDefined(
    fixture?.status_short,
    nestedFixture?.status_short,
    nestedStatus?.short,
    nestedStatus?.code,
    fixture?.status,
    fixture?.match_status,
    fixture?.fixture_status,
    ""
  );
  const status = String(rawStatus || "").toUpperCase();
  const elapsed = Number(
    firstDefined(
      fixture?.elapsed,
      fixture?.status_elapsed,
      fixture?.live_minute,
      fixture?.minute,
      nestedStatus?.elapsed
    )
  );
  const isFinished = FINISHED_STATUSES.includes(status);
  const isLive = LIVE_STATUSES.includes(status) || (!isFinished && Number.isFinite(elapsed) && elapsed > 0);
  return { rawStatus, status, elapsed, isFinished, isLive };
}

function getFixtureScore(fixture) {
  const nestedFixture = typeof fixture?.fixture === "object" && fixture?.fixture ? fixture.fixture : null;
  const home = firstDefined(
    fixture?.home_score,
    fixture?.goals_home,
    fixture?.score_home,
    fixture?.score?.home,
    fixture?.score?.fulltime?.home,
    fixture?.score?.full_time?.home,
    fixture?.scores?.home,
    fixture?.scores?.fulltime?.home,
    nestedFixture?.goals?.home,
    nestedFixture?.score?.home,
    nestedFixture?.score?.fulltime?.home,
    fixture?.goals?.home,
    fixture?.scores?.home
  );
  const away = firstDefined(
    fixture?.away_score,
    fixture?.goals_away,
    fixture?.score_away,
    fixture?.score?.away,
    fixture?.score?.fulltime?.away,
    fixture?.score?.full_time?.away,
    fixture?.scores?.away,
    fixture?.scores?.fulltime?.away,
    nestedFixture?.goals?.away,
    nestedFixture?.score?.away,
    nestedFixture?.score?.fulltime?.away,
    fixture?.goals?.away,
    fixture?.scores?.away
  );
  return {
    home: home == null ? null : Number(home),
    away: away == null ? null : Number(away),
  };
}

function mergeFixtureDetailPayload(payload) {
  const root = payload && typeof payload === "object" ? payload : {};
  const providerRow = Array.isArray(root?.response) ? root.response[0] || null : null;
  const baseFixture =
    (root?.fixture && typeof root.fixture === "object" ? root.fixture : null) ||
    (providerRow?.fixture && typeof providerRow.fixture === "object" ? providerRow.fixture : null) ||
    null;

  if (!baseFixture) return null;

  return {
    ...baseFixture,
    // Preserve richer sibling payload fields used by frontend UX.
    events: root?.events ?? providerRow?.events ?? baseFixture?.events ?? [],
    lineups: root?.lineups ?? providerRow?.lineups ?? baseFixture?.lineups ?? [],
    goals: root?.goals ?? providerRow?.goals ?? baseFixture?.goals,
    score: root?.score ?? providerRow?.score ?? baseFixture?.score,
    statistics: root?.statistics ?? providerRow?.statistics ?? baseFixture?.statistics,
    players: root?.players ?? providerRow?.players ?? baseFixture?.players,
    teams: root?.teams ?? providerRow?.teams ?? baseFixture?.teams,
    league: root?.league ?? providerRow?.league ?? baseFixture?.league,
    status: root?.status ?? baseFixture?.status ?? providerRow?.fixture?.status,
    elapsed:
      root?.elapsed ??
      baseFixture?.elapsed ??
      providerRow?.fixture?.status?.elapsed ??
      baseFixture?.status?.elapsed,
    home_score:
      root?.home_score ??
      baseFixture?.home_score ??
      root?.goals?.home ??
      providerRow?.goals?.home ??
      baseFixture?.goals?.home,
    away_score:
      root?.away_score ??
      baseFixture?.away_score ??
      root?.goals?.away ??
      providerRow?.goals?.away ??
      baseFixture?.goals?.away,
  };
}

function getExplanationCacheKey(rawMarket, formWindow) {
  return `${String(rawMarket || "").toUpperCase()}__${formWindow}`;
}

function buildOddsFingerprint(rows = []) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) =>
      [
        String(row?.market ?? ""),
        String(row?.bookmaker ?? ""),
        String(row?.selection ?? ""),
        String(row?.price ?? ""),
        String(row?.last_seen ?? ""),
      ].join("|")
    )
    .sort()
    .join("||");
}

function getLatestOddsLastSeen(rows = []) {
  let latest = null;
  for (const row of Array.isArray(rows) ? rows : []) {
    const raw = row?.last_seen;
    if (!raw) continue;
    const dt = new Date(raw);
    if (Number.isNaN(dt.getTime())) continue;
    if (!latest || dt.getTime() > latest.getTime()) latest = dt;
  }
  return latest;
}

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const toNum = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const extractLeagueTableRows = (payload) => {
  const isArrayRoot = Array.isArray(payload);
  const hasTable = Array.isArray(payload?.table);
  const hasStandings = Array.isArray(payload?.standings);
  const hasNestedStandings = Array.isArray(payload?.league?.standings);

  let rows = [];
  if (isArrayRoot) rows = payload;
  else if (hasTable) rows = payload.table;
  else if (hasStandings) rows = Array.isArray(payload.standings[0]) ? payload.standings[0] : payload.standings;
  else if (hasNestedStandings) {
    rows = Array.isArray(payload.league.standings[0]) ? payload.league.standings[0] : payload.league.standings;
  }

  return {
    rows: Array.isArray(rows) ? rows : [],
    hasTable,
    hasStandings: hasStandings || hasNestedStandings,
    validShape: isArrayRoot || hasTable || hasStandings || hasNestedStandings,
  };
};

const normalizeLeagueTableRows = (rows) => {
  return rows.map((row, idx) => {
    const teamName = firstDefined(row.team, row.team_name, row.name, row.team?.name, "Unknown");
    const gf = toNum(firstDefined(row.gf, row.goals_for, row.goals?.for, row.goals?.scored, row.all?.goals?.for));
    const ga = toNum(firstDefined(row.ga, row.goals_against, row.goals?.against, row.goals?.conceded, row.all?.goals?.against));
    return {
      position: firstDefined(row.position, row.rank, row.pos, row.place, idx + 1),
      team: teamName,
      team_id: firstDefined(row.team_id, row.id, row.team?.id, null),
      played: toNum(firstDefined(row.played, row.p, row.mp, row.all?.played)),
      win: toNum(firstDefined(row.win, row.w, row.wins, row.all?.win)),
      draw: toNum(firstDefined(row.draw, row.d, row.draws, row.all?.draw)),
      lose: toNum(firstDefined(row.lose, row.loss, row.losses, row.l, row.all?.lose)),
      gf,
      ga,
      points: toNum(firstDefined(row.points, row.pts, row.point, row.all?.points)),
      form: firstDefined(row.form, row.recent_form, row.last5, row.last_5, ""),
    };
  });
};

function RefereeProfilePanel({ profile }) {
  if (!profile?.available || !profile?.averages) return null;
  const initials = String(profile.referee || "R")
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("");
  const comparisonLabel = (value) => value ? `${value.charAt(0).toUpperCase()}${value.slice(1)} league average` : "Historical average";
  const comparisonColor = (value) => value === "above" ? "#16a34a" : value === "below" ? "#d97706" : "#64748b";
  const metric = (icon, label, value, comparison, iconColor) => (
    <div style={{ flex: "1 1 220px", padding: 18, borderTop: "1px solid rgba(15,118,62,.12)" }}>
      <div style={{ color: iconColor, fontSize: 14, marginBottom: 7 }}>{icon}</div>
      <div style={{ color: "#64748b", fontSize: 10, fontWeight: 900, letterSpacing: 1.2 }}>{label}</div>
      <div style={{ color: "#0f172a", fontSize: 28, fontWeight: 900, marginTop: 7 }}>{Number(value).toFixed(1)} <span style={{ color: "#64748b", fontSize: 12 }}>/ match</span></div>
      <div style={{ color: comparisonColor(comparison), fontSize: 12, fontWeight: 800, marginTop: 5 }}>{comparisonLabel(comparison)}</div>
    </div>
  );
  return (
    <section style={{ margin: "14px 0 18px", borderRadius: 18, overflow: "hidden", background: "linear-gradient(145deg,#fff,#f4fbf6)", border: "1px solid rgba(22,163,74,.24)", boxShadow: "0 12px 34px rgba(15,118,62,.08)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 13, padding: 18 }}>
        <div style={{ width: 52, height: 52, borderRadius: "50%", display: "grid", placeItems: "center", flexShrink: 0, color: "#166534", background: "rgba(22,163,74,.1)", border: "1px solid rgba(22,163,74,.26)", fontWeight: 900 }}>{initials}</div>
        <div>
          <div style={{ color: "#16a34a", fontSize: 10, fontWeight: 900, letterSpacing: 1.6 }}>REFEREE PROFILE</div>
          <h3 style={{ margin: "3px 0 0", color: "#0f172a", fontSize: 20 }}>{profile.referee}</h3>
          <div style={{ color: "#64748b", fontSize: 12, marginTop: 3 }}>{profile.country ? `${profile.country} · ` : ""}Last {profile.sample_size} cached appointments</div>
        </div>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap" }}>
        {metric("■", "YELLOW CARDS", profile.averages.yellow_cards, profile.comparison?.yellow_cards, "#eab308")}
        {metric("●", "FOULS CALLED", profile.averages.fouls, profile.comparison?.fouls, "#16a34a")}
      </div>
      <div style={{ padding: "13px 18px", background: "rgba(22,163,74,.06)", borderTop: "1px solid rgba(22,163,74,.12)", color: "#334155", fontSize: 13, fontWeight: 650 }}>{profile.insight}</div>
    </section>
  );
}

export default function FootballMatchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fixtureIdNum = parseInt(id, 10);

  const auth = useAuth();
  const isAdmin = !!auth?.isAdmin;
  const analyticsAllowed = canViewFootballAnalytics(isAdmin);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTabState] = useState(() => {
    const sub = searchParams.get("sub");
    if (sub && sectionOf(sub)) return sub;
    const sec = SECTIONS.find((x) => x.id === searchParams.get("tab"));
    return sec ? subsFor(sec.id, analyticsAllowed)[0]?.id || "Preview" : "Preview";
  });
  const setActiveTab = (key) => {
    setActiveTabState(key);
    const sec = sectionOf(key);
    if (sec) {
      const isDefault = subsFor(sec.id, analyticsAllowed)[0]?.id === key;
      setSearchParams({ tab: sec.id, ...(isDefault ? {} : { sub: key }) }, { replace: true });
    }
  };
  const matchIntel = useMatchIntelligence(id);
  const pitchMatchId = matchIntel.data?.status === "AVAILABLE" ? matchIntel.data.profile?.id : null;

  const [primaryFixture, setFixture] = useState(null);
  const fixture = useMemo(() => recordedFixture(primaryFixture,matchIntel.data),[primaryFixture,matchIntel.data]);
  const [odds, setOdds] = useState([]);
  const [edges, setEdges] = useState([]);
  const [edgesMeta, setEdgesMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState(null);
  const [formN, setFormN] = useState(5);
  const [leagueTable, setLeagueTable] = useState([]);
  const [leagueTableGroupName, setLeagueTableGroupName] = useState("");
  const [leagueTableLoading, setLeagueTableLoading] = useState(false);
  const [leagueTableError, setLeagueTableError] = useState("");
  const [leagueFixtures, setLeagueFixtures] = useState([]);

  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [refereeProfile, setRefereeProfile] = useState(null);

  const [h2hData, setH2hData] = useState(null);
  const [h2hLoading, setH2hLoading] = useState(false);

  const [liveData, setLiveData] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveError, setLiveError] = useState("");
  const [liveRefreshNotice, setLiveRefreshNotice] = useState("");
  const [lastLiveRefreshAt, setLastLiveRefreshAt] = useState(null);

  const [explanations, setExplanations] = useState({});
  const [expandedWhy, setExpandedWhy] = useState(null);
  const [expandedWhyMarket, setExpandedWhyMarket] = useState(null);
  const [explainFormWindow, setExplainFormWindow] = useState(5);
  const [explanationLoadingKey, setExplanationLoadingKey] = useState("");
  const [explanationErrors, setExplanationErrors] = useState({});

  const [selectedBookmaker, setSelectedBookmaker] = useState("All");
  // The overview is the compact view; every other tab shows its full content.
  const isSimpleMode = activeTab === "Preview";

  // Load main fixture data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${API_BASE}/api/fixtures/id/${id}/json`, { cache: "no-store" });
        if (!res.ok) throw new Error(`Fixture not found (${res.status})`);
        const data = await res.json();

        setFixture(mergeFixtureDetailPayload(data));
        setOdds(data.odds || []);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  useEffect(() => {
    if (!fixtureIdNum || !fixture?.referee) {
      setRefereeProfile(null);
      return undefined;
    }
    let cancelled = false;
    api.get("/football/referee-profile", { params: { fixture_id: fixtureIdNum } })
      .then(({ data }) => { if (!cancelled) setRefereeProfile(data?.available ? data : null); })
      .catch(() => { if (!cancelled) setRefereeProfile(null); });
    return () => { cancelled = true; };
  }, [fixtureIdNum, fixture?.referee]);

  // Load form data
  useEffect(() => {
    if (!fixtureIdNum) return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data: fj } = await api.get(`/form/form/fixture`, {
          params: { fixture_id: fixtureIdNum, n: formN, all_comps: 1 }
        });
        if (!cancelled) {
          setFormData({
            home: fj.home ?? { summary: fj.home_form ?? {}, recent: fj.home_recent ?? [] },
            away: fj.away ?? { summary: fj.away_form ?? {}, recent: fj.away_recent ?? [] },
            n: fj.n ?? formN,
          });
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Form fetch failed:", err);
          setFormData(null);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [fixtureIdNum, formN]);

  const leagueFetchContext = useMemo(() => {
    const leagueCode =
      firstDefined(
        fixture?.league_code,
        fixture?.comp_code,
        fixture?.competition_code,
        fixture?.comp
      ) || null;
    const leagueId = firstDefined(
      fixture?.league_id,
      fixture?.provider_league_id,
      fixture?.api_league_id,
      null
    );
    const seasonValue = firstDefined(fixture?.season, fixture?.league_season, fixture?.year, null);
    const worldCup = isWorldCupCompetition({
      league_code: leagueCode,
      league_id: leagueId,
      league_name: firstDefined(fixture?.league_name, fixture?.competition, fixture?.comp),
    });
    return {
      leagueCode: worldCup ? WORLD_CUP_CODE : leagueCode,
      leagueId,
      seasonValue: worldCup ? WORLD_CUP_SEASON : seasonValue,
      worldCup,
    };
  }, [fixture]);

  const fetchLeagueTable = async () => {
    const { leagueCode, leagueId, seasonValue, worldCup } = leagueFetchContext;
    if (!leagueCode && !leagueId) {
      setLeagueTable([]);
      setLeagueTableError("No league context for this fixture.");
      return;
    }

    setLeagueTableLoading(true);
    setLeagueTableError("");
    setLeagueTableGroupName("");

    if (worldCup) {
      try {
        const { data } = await api.get("/api/league/table", {
          params: { league_code: WORLD_CUP_CODE, season: WORLD_CUP_SEASON },
        });
        const groups = normalizeWorldCupGroups(data);
        const fixtureGroup = findWorldCupFixtureGroup(groups, fixture);
        if (!fixtureGroup) {
          throw new Error("Could not find this fixture's World Cup group.");
        }
        setLeagueTable(fixtureGroup.rows);
        setLeagueTableGroupName(fixtureGroup.name);
        setLeagueTableLoading(false);
        return;
      } catch (err) {
        setLeagueTable([]);
        setLeagueTableError(
          err?.response?.data?.detail || err?.message || "Could not load the World Cup group table."
        );
        setLeagueTableLoading(false);
        return;
      }
    }

    const attempts = [];
    const normalizedLeagueCode = leagueCode ? String(leagueCode).trim().toUpperCase() : null;

    if (normalizedLeagueCode) {
      // Required fallback chain:
      // 1) /api/fixtures/league/table?league=...&season=...
      // 2) /api/league/table?league_code=...&season=...
      // 3) /api/fixtures/league/table?league=... (no season)
      // 4) /api/league/table?league_code=... (no season)
      attempts.push({
        endpoint: "/api/fixtures/league/table",
        params: {
          league: normalizedLeagueCode,
          ...(seasonValue ? { season: seasonValue } : {}),
        },
      });
      attempts.push({
        endpoint: "/api/league/table",
        params: {
          league_code: normalizedLeagueCode,
          ...(seasonValue ? { season: seasonValue } : {}),
        },
      });
      if (seasonValue) {
        attempts.push({
          endpoint: "/api/fixtures/league/table",
          params: {
            league: normalizedLeagueCode,
          },
        });
        attempts.push({
          endpoint: "/api/league/table",
          params: {
            league_code: normalizedLeagueCode,
          },
        });
      }
    } else if (leagueId) {
      // ID-only fallback path if no code is available.
      attempts.push({
        endpoint: "/api/fixtures/league/table",
        params: {
          league_id: leagueId,
          ...(seasonValue ? { season: seasonValue } : {}),
        },
      });
    }

    let lastError = null;
    for (const attempt of attempts) {
      try {
        if (LEAGUE_TABLE_DEBUG) {
          console.log("[LeagueTable][attempt]", attempt.endpoint, attempt.params);
        }
        const { data } = await api.get(attempt.endpoint, { params: attempt.params });
        const extracted = extractLeagueTableRows(data);
        const rows = normalizeLeagueTableRows(extracted.rows);
        if (LEAGUE_TABLE_DEBUG) {
          console.log("[LeagueTable][response]", {
            endpoint: attempt.endpoint,
            params: attempt.params,
            status: 200,
            hasTable: extracted.hasTable,
            hasStandings: extracted.hasStandings,
            responseKeys: data && typeof data === "object" ? Object.keys(data) : [],
            rows: rows.length,
          });
        }
        if (!extracted.validShape) {
          lastError = new Error("Invalid standings payload shape");
          continue;
        }
        setLeagueTable(rows);
        setLeagueTableGroupName("");
        setLeagueTableError("");
        setLeagueTableLoading(false);
        return;
      } catch (err) {
        lastError = err;
        if (LEAGUE_TABLE_DEBUG) {
          console.log("[LeagueTable][failed]", {
            endpoint: attempt.endpoint,
            params: attempt.params,
            status: err?.response?.status || null,
            message: err?.message,
          });
        }
      }
    }

    setLeagueTable([]);
    setLeagueTableGroupName("");
    setLeagueTableError(
      lastError?.response?.data?.detail ||
        lastError?.message ||
        "Could not load league table."
    );
    setLeagueTableLoading(false);
  };

  // Load league table
  useEffect(() => {
    fetchLeagueTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    leagueFetchContext.leagueCode,
    leagueFetchContext.leagueId,
    leagueFetchContext.seasonValue,
    leagueFetchContext.worldCup,
  ]);

  // Load league fixtures
  useEffect(() => {
    if (!fixture?.round) return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data: json } = await api.get(`/api/fixtures/round?round=${encodeURIComponent(fixture.round)}`);
        if (!cancelled) setLeagueFixtures(Array.isArray(json) ? json : []);
      } catch (err) {
        if (!cancelled) setLeagueFixtures([]);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [fixture]);

  // Load edges
  useEffect(() => {
    const loadEdges = async () => {
      if (!fixtureIdNum) return;
      try {
        const json = await fetchFixtureEdges(fixtureIdNum);
        const fullEdges = json.edges || json.edges_teaser || [];

        setEdges(
          Array.isArray(fullEdges)
            ? fullEdges.filter((edge) => isAllowedPublicEdge(edge?.edge))
            : []
        );
        setEdgesMeta({
          isPremium: !!json.is_premium,
          hasAccess: !!json.has_access,
          usedToday: json.used_today ?? 0,
          limit: json.limit ?? 0,
          hasFullAccess: !!(json.is_premium || json.has_access),
          isTeaser: !json.is_premium && !json.has_access,
          authMismatch: !!json?._auth?.authMismatch,
          signedIn: !!json?._auth?.signedIn,
          meIsPremium: !!json?._auth?.meIsPremium,
          retriedWithFreshToken: !!json?._auth?.retriedWithFreshToken,
        });
      } catch (err) {
        console.error("Error fetching edges:", err);
        setEdges([]);
        setEdgesMeta(null);
      }
    };
    if (fixtureIdNum) loadEdges();
  }, [fixtureIdNum]);

  // Load match insights
  useEffect(() => {
    const loadInsights = async () => {
      if (!fixtureIdNum) return;
      setInsightsLoading(true);
      try {
        const { data: json } = await api.get(
          `/api/fixtures/${fixtureIdNum}/insights`,
          { params: { model: "team_form" } }
        );
        setInsights(json || null);
      } catch (err) {
        console.error("Error fetching match insights:", err);
        setInsights(null);
      } finally {
        setInsightsLoading(false);
      }
    };

    loadInsights();
  }, [fixtureIdNum]);

  // Load H2H data
  useEffect(() => {
    const loadH2H = async () => {
      if (!fixtureIdNum) return;
      setH2hLoading(true);
      try {
        const { data: json } = await api.get(`/football/h2h/${fixtureIdNum}`);
        setH2hData(json || null);
      } catch (err) {
        console.error("Error fetching H2H data:", err);
        setH2hData(null);
      } finally {
        setH2hLoading(false);
      }
    };

    loadH2H();
  }, [fixtureIdNum]);

  // Load live in-play model (lazy)
  useEffect(() => {
    if (activeTab !== "Live Odds" || !fixtureIdNum) return;
    let cancelled = false;
    const load = async () => {
      setLiveLoading(true);
      setLiveError("");
      try {
        const { data: json } = await api.get(`/api/fixtures/id/${fixtureIdNum}/inplay`);
        if (!cancelled) setLiveData(json || null);
      } catch (err) {
        if (!cancelled) {
          console.error("Live fetch failed:", err);
          setLiveError("Could not fetch live data.");
          setLiveData(null);
        }
      } finally {
        if (!cancelled) setLiveLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [activeTab, fixtureIdNum]);

  const refreshLiveOdds = async (source = "manual") => {
    if (!fixtureIdNum) return;
    const ts = Date.now();
    const previousFingerprint = buildOddsFingerprint(odds);

    if (LIVE_REFRESH_DEBUG) {
      console.log("[LiveRefresh] click", {
        source,
        routeFixtureId: fixtureIdNum,
        pageFixtureId: fixture?.id,
      });
    }

    setLiveLoading(true);
    setLiveError("");
    setLiveRefreshNotice("");

    try {
      if (LIVE_REFRESH_DEBUG) {
        console.log("[LiveRefresh] request:start", { fixtureId: fixtureIdNum, ts });
      }

      const [inplayRes, fixtureRes] = await Promise.all([
        api.get(`/api/fixtures/id/${fixtureIdNum}/inplay`, { params: { _ts: ts } }),
        fetch(`${API_BASE}/api/fixtures/id/${id}/json?_ts=${ts}`, {
          cache: "no-store",
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        }),
      ]);

      if (!fixtureRes.ok) {
        throw new Error(`Failed to refresh fixture odds (${fixtureRes.status})`);
      }

      const fixtureJson = await fixtureRes.json();
      const freshOdds = Array.isArray(fixtureJson?.odds) ? fixtureJson.odds : [];
      const freshFingerprint = buildOddsFingerprint(freshOdds);

      setLiveData(inplayRes?.data || null);
      setOdds(freshOdds);
      if (fixtureJson) {
        const mergedFixture = mergeFixtureDetailPayload(fixtureJson);
        if (mergedFixture) setFixture(mergedFixture);
      }

      setLastLiveRefreshAt(new Date());
      setLiveRefreshNotice(
        freshFingerprint === previousFingerprint
          ? "No price change detected."
          : "Updated just now."
      );

      if (LIVE_REFRESH_DEBUG) {
        console.log("[LiveRefresh] response:received", {
          fixtureId: fixtureIdNum,
          oddsCount: freshOdds.length,
          changed: freshFingerprint !== previousFingerprint,
          latestLastSeen: getLatestOddsLastSeen(freshOdds)?.toISOString() || null,
        });
      }
    } catch (err) {
      console.error("Live refresh failed:", err);
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        "Could not refresh live odds.";
      setLiveError(message);
      setLiveRefreshNotice("");
    } finally {
      setLiveLoading(false);
    }
  };

  const fetchExplanation = async (rawMarket, index, options = {}) => {
    const force = Boolean(options.force);
    const edge = options.edge || {};
    const cacheKey = getExplanationCacheKey(rawMarket, explainFormWindow);
    if (!force && (getRowWhy(edge) || isStatPropMarket(rawMarket))) {
      const isSameOpen = expandedWhy === index && expandedWhyMarket === rawMarket;
      setExpandedWhy(isSameOpen ? null : index);
      setExpandedWhyMarket(isSameOpen ? null : rawMarket);
      setExplanationErrors((prev) => ({ ...prev, [cacheKey]: "" }));
      return;
    }
    if (!force && explanations[cacheKey]) {
      const isSameOpen = expandedWhy === index && expandedWhyMarket === rawMarket;
      setExpandedWhy(isSameOpen ? null : index);
      setExpandedWhyMarket(isSameOpen ? null : rawMarket);
      return;
    }
    const market = normalizeExplainMarket(rawMarket);

    try {
      setExplanationLoadingKey(cacheKey);
      setExplanationErrors((prev) => ({ ...prev, [cacheKey]: "" }));
      const { data: json } = await api.get(`/explain/probability`, {
        params: { fixture_id: id, market, form_n: explainFormWindow },
      });
      setExplanations((prev) => ({ ...prev, [cacheKey]: json }));
      setExpandedWhy(index);
      setExpandedWhyMarket(rawMarket);
    } catch (err) {
      console.error("Error loading explanation:", err);
      setExplanationErrors((prev) => ({
        ...prev,
        [cacheKey]: err?.response?.data?.detail || err?.message || "Could not load explanation.",
      }));
      setExpandedWhy(index);
      setExpandedWhyMarket(rawMarket);
    } finally {
      setExplanationLoadingKey((prev) => (prev === cacheKey ? "" : prev));
    }
  };

  useEffect(() => {
    if (expandedWhy === null || !expandedWhyMarket) return;
    if (isStatPropMarket(expandedWhyMarket)) return;
    fetchExplanation(expandedWhyMarket, expandedWhy, { force: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [explainFormWindow]);

  useEffect(() => {
    const topPreviewEdges =
      selectedBookmaker === "All"
        ? edges.slice(0, 3)
        : edges.filter((e) => e.bookmaker === selectedBookmaker).slice(0, 3);

    if (!isSimpleMode || activeTab !== "Preview" || !topPreviewEdges.length) return;
    let cancelled = false;

    const prefetchTopEdgeExplanations = async () => {
      for (const edge of topPreviewEdges) {
        if (getRowWhy(edge) || isStatPropMarket(edge.market)) continue;
        const cacheKey = getExplanationCacheKey(edge.market, explainFormWindow);
        if (explanations[cacheKey]) continue;
        try {
          const market = normalizeExplainMarket(edge.market);
          const { data: json } = await api.get(`/explain/probability`, {
            params: { fixture_id: id, market, form_n: explainFormWindow },
          });
          if (cancelled) return;
          setExplanations((prev) => (prev[cacheKey] ? prev : { ...prev, [cacheKey]: json }));
          setExplanationErrors((prev) => ({ ...prev, [cacheKey]: "" }));
        } catch (err) {
          if (cancelled) return;
          setExplanationErrors((prev) => ({
            ...prev,
            [cacheKey]:
              prev[cacheKey] ||
              err?.response?.data?.detail ||
              err?.message ||
              "Could not load explanation.",
          }));
        }
      }
    };

    prefetchTopEdgeExplanations();
    return () => {
      cancelled = true;
    };
  }, [activeTab, edges, explainFormWindow, explanations, id, isSimpleMode, selectedBookmaker]);

  const formatTimeUTC = (isoString) => {
    if (!isoString) return "—";
    const safe = /Z$|[+-]\d{2}:\d{2}$/.test(isoString) ? isoString : isoString + "Z";
    try {
      const d = new Date(safe);
      return d.toLocaleString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        day: "2-digit",
        month: "short",
        timeZone: "UTC",
      });
    } catch {
      return isoString;
    }
  };

  if (loading) {
    return (
      <div className="fa">
        <div className="fa-wrap" style={{ paddingTop: 24 }}>
          <div className="fa-skeleton" role="status" aria-label="Loading match details" style={{ height: 150 }} />
          <div className="fa-skeleton" style={{ height: 260 }} />
        </div>
      </div>
    );
  }

  if (error || !fixture) {
    return (
      <div className="fa">
        <div className="fa-wrap" style={{ paddingTop: 24 }}>
          <button type="button" className="fa-back" onClick={() => navigate("/")} aria-label="Back to fixtures">←</button>
          <p className="fa-empty" role="alert">{error || "Match not found."}</p>
          <RecordedMatchFallback enabled={matchIntel.enabled} data={matchIntel.data} />
        </div>
      </div>
    );
  }

  if ((fixture.sport || "").toLowerCase() === "rugby") {
    return <Navigate to={`/rugby/match/${id}`} replace />;
  }

  const homeTeamId =
    fixture.provider_home_team_id ??
    fixture.home_team_id ??
    fixture.home_id ??
    fixture.homeTeamId ??
    fixture?.teams?.home?.id ??
    null;
  const awayTeamId =
    fixture.provider_away_team_id ??
    fixture.away_team_id ??
    fixture.away_id ??
    fixture.awayTeamId ??
    fixture?.teams?.away?.id ??
    null;
  const homeTeamName = fixture.home_team ?? fixture?.teams?.home?.name ?? "Home";
  const awayTeamName = fixture.away_team ?? fixture?.teams?.away?.name ?? "Away";
  const homeTeamLogo = firstDefined(
    fixture?.home_team_logo,
    fixture?.home_logo,
    fixture?.teams?.home?.logo,
    fixture?.home_team?.logo,
    `/logos/${slugifyTeamName(homeTeamName)}.png`
  );
  const awayTeamLogo = firstDefined(
    fixture?.away_team_logo,
    fixture?.away_logo,
    fixture?.teams?.away?.logo,
    fixture?.away_team?.logo,
    `/logos/${slugifyTeamName(awayTeamName)}.png`
  );
  const season = fixture.season ?? fixture.league_season ?? fixture.year ?? null;
  const leagueId = fixture.league_id ?? fixture.provider_league_id ?? fixture.api_league_id ?? null;
  const leagueCode = getLeagueCodeFromAny({
    code: fixture.league_code ?? fixture.comp_code ?? fixture.competition_code,
    name: fixture.league_name ?? fixture.comp,
    id: leagueId,
  });
  const leagueDisplayName =
    fixture.league_name ||
    getLeagueNameFromAny({ code: leagueCode, name: fixture.comp, id: leagueId }) ||
    fixture.comp ||
    "FOOTBALL";

  const grouped = Array.isArray(odds)
    ? odds.reduce((acc, o) => {
        (acc[o.market] ||= []).push(o);
        return acc;
      }, {})
    : {};

  const uniqueBookmakers = [...new Set(edges.map((e) => e.bookmaker))];
  const filteredEdges = selectedBookmaker === "All" ? edges : edges.filter((e) => e.bookmaker === selectedBookmaker);
  const previewEdges = isSimpleMode ? filteredEdges.slice(0, 3) : filteredEdges;
  const latestOddsSeen = getLatestOddsLastSeen(odds);
  const fixtureEvents = sortEventsAsc(normalizeEventRows(fixture?.events || []));
  const fixtureLineupsFallback = fixture?.lineups || null;
  const score = getFixtureScore(fixture);
  const statusMeta = getFixtureStatusMeta(fixture);
  const hasScore = Number.isFinite(score.home) && Number.isFinite(score.away);
  const statusLabel = statusMeta.isFinished
    ? "FT"
    : statusMeta.isLive
      ? (String(statusMeta.status) === "HT" ? "HT" : Number.isFinite(statusMeta.elapsed) ? `${statusMeta.elapsed}'` : "LIVE")
      : "Upcoming";
  const section = sectionOf(activeTab)?.id || "overview";

  const clubHref = (teamId, teamName) => {
    const slug = slugifyTeamName(teamName);
    const params = new URLSearchParams();
    if (season) params.set("season", String(season));
    if (leagueId) params.set("league", String(leagueId));
    const qs = params.toString();
    return `/club/${teamId}/${slug}${qs ? `?${qs}` : ""}`;
  };

  const subs = subsFor(section, analyticsAllowed);
  const LEGACY_VIEWS = ["Preview", "Timeline", "Events", "Players", "Injuries", "Props", "Form", "H2H", "Table", "Insights", "Edges", "Live Odds", "Odds", "Predictions", "Community", "News"];
  const legacyVisible = LEGACY_VIEWS.includes(activeTab);
  const goSection = (sectionId) => {
    const first = subsFor(sectionId, analyticsAllowed)[0];
    if (first) setActiveTab(first.id);
  };
  const openProfile = (href) => navigate(href);

  return (
    <div className="fa">
      <MatchHeaderCompact
        home={{ name: homeTeamName, logo: homeTeamLogo, href: homeTeamId ? clubHref(homeTeamId, homeTeamName) : null }}
        away={{ name: awayTeamName, logo: awayTeamLogo, href: awayTeamId ? clubHref(awayTeamId, awayTeamName) : null }}
        score={hasScore ? score : null}
        statusLabel={statusLabel}
        isLive={statusMeta.isLive}
        competition={leagueDisplayName}
        kickoff={formatTimeUTC(fixture.kickoff_utc)}
        onBack={() => navigate("/")}
        onNavigate={navigate}
        homeGoals={goalLines(fixtureEvents, homeTeamId)}
        awayGoals={goalLines(fixtureEvents, awayTeamId)}
      />
      {fixture.recorded_display_source&&<p className="fa-note fa-wrap">Recorded final result and events: PitchAPI. Cached lineups: {fixture.recorded_lineup_source === 'api_football' ? 'API-Football' : 'PitchAPI'}. This is post-match data, not a live feed.</p>}
      <ScrollTabs tabs={SECTIONS} value={section} onChange={goSection} label="Match sections" idPrefix="fa-match" />
      <div className="fa-wrap" role="tabpanel" id={`fa-match-panel-${section}`} aria-labelledby={`fa-match-tab-${section}`}>
        <Segmented options={subs} value={activeTab} onChange={setActiveTab} label={`${section} views`} />

        {activeTab === "Preview" && (
          <>
            <KeyEvents events={fixtureEvents} homeTeamId={homeTeamId} homeName={homeTeamName} awayName={awayTeamName} />
            <OverviewStats recorded={matchIntel.data} fixtureId={fixtureIdNum} homeName={homeTeamName} awayName={awayTeamName} onSeeAll={() => setActiveTab("Stats")} />
          </>
        )}
        {activeTab === "Lineups" && (
          <MatchLineups
            fixtureId={id}
            providerFixtureId={fixture.provider_fixture_id}
            fallbackLineups={fixtureLineupsFallback}
            events={fixtureEvents}
            isAdmin={isAdmin}
            analyticsAllowed={analyticsAllowed}
            pitchMatchId={pitchMatchId}
            onNavigate={openProfile}
          />
        )}
        {activeTab === "Stats" && <FullStats recorded={matchIntel.data} fixtureId={fixtureIdNum} homeName={homeTeamName} awayName={awayTeamName} />}
        {activeTab === "Advanced" &&
          (matchIntel.loading ? (
            <div className="fa-skeleton" role="status" aria-label="Loading match analysis" style={{ height: 240 }} />
          ) : matchIntel.error ? (
            <div className="fa-empty">{matchIntel.error}</div>
          ) : (
            <MatchAnalysis data={matchIntel.data} />
          ))}

        {legacyVisible && (
          <div className={`fa-legacy ${activeTab === "Preview" ? "flush" : ""}`}>
            <div>
              <div>
            {activeTab === "Preview" && (
              <>
                <MatchPulse
                  formData={formData}
                  homeName={homeTeamName}
                  awayName={awayTeamName}
                  n={formData?.n || formN}
                  onOpenForm={() => {
                    setActiveTab("Form");
                  }}
                />

                <RefereeProfilePanel profile={refereeProfile} />

                <HighlightVideosSection
                  mode="fixture"
                  fixtureId={fixtureIdNum}
                  title="Highlights"
                  limit={4}
                  sinceDays={45}
                  dark
                  featured
                  hideWhenEmpty
                />

                {!isSimpleMode && (
                  <div style={{ margin: "12px 0 16px" }}>
                    <MatchInsights
                      fixtureId={fixtureIdNum}
                      fixture={fixture}
                      insights={insights}
                      loading={insightsLoading}
                      limit={4}
                    />
                  </div>
                )}

                {!isSimpleMode && (
                  <div
                    style={{
                      margin: "12px 0 16px",
                      padding: "10px 12px",
                      borderRadius: 12,
                      border: "1px solid #ddd",
                      background: "#fff",
                      color: "#000",
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ fontSize: 13, flex: 1, minWidth: 200, color: "#000" }}>
                      <strong>Player stats & props</strong>
                      <br />
                      See shots, cards and season breakdowns for every player in this match.
                    </div>
                    <button
                      onClick={() => setActiveTab("Players")}
                      style={{
                        padding: "6px 12px",
                        borderRadius: 999,
                        border: "1px solid rgba(34,197,94,0.9)",
                        background: "rgba(22,163,74,0.9)",
                        color: "#fff",
                        fontSize: 13,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Open player stats →
                    </button>
                  </div>
                )}

                <div
                  style={{
                    background: C.cardBg,
                    borderRadius: 12,
                    padding: 20,
                    marginBottom: 20,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                      marginBottom: 8,
                    }}
                  >
                    <h3 style={{ color: "#000", margin: 0 }}>Best Edges</h3>
                    {edgesMeta && (
                      <FixtureAccessPill
                        meta={edgesMeta}
                        fixtureId={fixtureIdNum}
                        variant="default"
                      />
                    )}
                  </div>

                  {edgesMeta && (
                    <p style={{ fontSize: 13, color: C.textSub, marginBottom: 12 }}>
                      {edgesMeta.hasFullAccess ? (
                        edgesMeta.isPremium ? (
                          <>
                            You're on <b>CSB Premium</b> – full model edges unlocked for this fixture.
                          </>
                        ) : (
                          <>
                            This fixture is unlocked. You've used <b>{edgesMeta.usedToday}</b> /{" "}
                            <b>{edgesMeta.limit}</b> free fixture unlocks today.
                          </>
                        )
                      ) : (
                        edgesMeta.authMismatch ? (
                          <>
                            You&apos;re signed in and your Premium status was detected, but this
                            edges request still came back as a free preview. Try refreshing this
                            page or signing out and back in on this device to refresh the session.
                          </>
                        ) : (
                          <>
                            Free preview only – showing a small sample of edges. You&apos;ve used{" "}
                            <b>{edgesMeta.usedToday}</b> / <b>{edgesMeta.limit}</b> free fixture unlocks today.{" "}
                            <Link to="/premium" style={{ color: "#FBBF24" }}>
                              Go Premium →
                            </Link>
                          </>
                        )
                      )}
                    </p>
                  )}

                  {uniqueBookmakers.length > 1 && (
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 13, color: C.textSub, marginRight: 8 }}>Filter by bookmaker: </label>
                      <select
                        value={selectedBookmaker}
                        onChange={(e) => setSelectedBookmaker(e.target.value)}
                        style={{
                          padding: "4px 8px",
                          borderRadius: 6,
                          border: `1px solid ${C.accentLight}`,
                          fontSize: 13,
                        }}
                      >
                        <option value="All">All</option>
                        {uniqueBookmakers.map((bm) => (
                          <option key={bm} value={bm}>
                            {bm}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 13, color: C.textSub, marginRight: 8 }}>Form window: </label>
                      <select
                        value={explainFormWindow}
                        onChange={(e) => setExplainFormWindow(Number(e.target.value))}
                        style={{
                          padding: "4px 8px",
                          borderRadius: 6,
                          border: `1px solid ${C.accentLight}`,
                          fontSize: 13,
                        }}
                      >
                        <option value={5}>Last 5</option>
                        <option value={10}>Last 10</option>
                        <option value={15}>Last 15</option>
                      </select>
                    </div>
                    {previewEdges.map((e, i) => {
                      const key = `${e.market}-${e.bookmaker}-${i}`;
                      const cacheKey = getExplanationCacheKey(e.market, explainFormWindow);
                      const exp = explanations[cacheKey];
                      const expErr = explanationErrors[cacheKey];
                      const isExpLoading = explanationLoadingKey === cacheKey;
                      const rowWhy = getRowWhy(e);
                      const statPropMarket = isStatPropMarket(e.market);
                      const showGenericExplanation = !statPropMarket && !rowWhy;
                      const eg = showGenericExplanation ? exp?.form?.expected_goals : null;
                      const notes = getExplainNotes(exp, e);
                      const hasCupBadge = showGenericExplanation && hasCupContext(notes);

                      return (
                        <div
                          key={key}
                          style={{
                            padding: "12px",
                            borderRadius: 8,
                            background: C.greenLt,
                            border: `1px solid ${C.accentLight}`,
                            marginBottom: 10,
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                            <div style={{ color: "#000" }}>
                              <strong style={{ color: "#000" }}>{formatMarketName(e.market)}</strong> —{" "}
                              {e.bookmaker} @ <strong>{formatOddsBoth(e.price)}</strong>{" "}
                              {typeof e.edge === "number" && typeof e.prob === "number" ? (
                                <span style={{ color: "#000" }}>
                                  ({(e.edge * 100).toFixed(1)}% edge, model p {(e.prob * 100).toFixed(1)}%)
                                </span>
                              ) : null}
                            </div>
                            {!isSimpleMode && (
                              <div style={{ display: "flex", gap: 8 }}>
                                <button
                                  onClick={() => fetchExplanation(e.market, i, { edge: e })}
                                  style={{
                                    padding: "4px 10px",
                                    borderRadius: 6,
                                    border: `1px solid ${C.pitchLine}`,
                                    background: C.cardBg,
                                    color: "#000",
                                    fontSize: 12,
                                    cursor: "pointer",
                                    fontWeight: 600,
                                  }}
                                >
                                  {isExpLoading && expandedWhy === i ? "Loading…" : "Why?"}
                                </button>
                              </div>
                            )}
                          </div>

                          {isSimpleMode && (
                            <EdgeExplanationCard
                              edge={e}
                              expectedGoals={eg}
                              notes={notes}
                              recommendation={showGenericExplanation ? exp?.recommendation : null}
                              formWindow={exp?.form_window || explainFormWindow}
                              showFormContext={showGenericExplanation}
                              cupContext={hasCupBadge}
                              loading={isExpLoading}
                              error={expErr}
                              formatText={humanizeExplainText}
                              compact
                            />
                          )}

                          {!isSimpleMode && expandedWhy === i && (
                            <EdgeExplanationCard
                              edge={e}
                              expectedGoals={eg}
                              notes={notes}
                              recommendation={showGenericExplanation ? exp?.recommendation : null}
                              formWindow={exp?.form_window || explainFormWindow}
                              showFormContext={showGenericExplanation}
                              cupContext={hasCupBadge}
                              loading={isExpLoading}
                              error={expErr}
                              onRetry={() => fetchExplanation(e.market, i, { force: true, edge: e })}
                              formatText={humanizeExplainText}
                            />
                          )}
                        </div>
                      );
                    })}
                    {isSimpleMode && filteredEdges.length > previewEdges.length && (
                      <div style={{ marginTop: 8, display: "flex", justifyContent: "center" }}>
                        <button
                          onClick={() => {
                            setActiveTab("Edges");
                          }}
                          style={{
                            padding: "8px 14px",
                            borderRadius: 999,
                            border: `1px solid ${C.pitchLine}`,
                            background: C.cardBg,
                            color: "#000",
                            fontSize: 13,
                            fontWeight: 700,
                            cursor: "pointer",
                          }}
                        >
                          Show full analysis ({filteredEdges.length} edges)
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {!isSimpleMode && (
                  <div style={{ overflowX: "auto" }}>
                    <OddsTable grouped={grouped} />
                  </div>
                )}

              </>
            )}

            {activeTab === "Timeline" && (
              <MatchTimeline
                events={fixtureEvents}
                statusMeta={statusMeta}
                homeTeamName={homeTeamName}
                awayTeamName={awayTeamName}
              />
            )}

            {activeTab === "News" && (
              <div
                style={{
                  background: C.cardBg,
                  borderRadius: 12,
                  padding: 20,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                }}
              >
                <SportsNewsSection
                  mode="fixture"
                  fixtureId={fixtureIdNum}
                  title="Match News"
                  limit={6}
                  dark={false}
                />
              </div>
            )}

            {activeTab === "Edges" && (
              <div
                style={{
                  background: C.cardBg,
                  borderRadius: 12,
                  padding: 20,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    marginBottom: 12,
                  }}
                >
                  <h3 style={{ color: "#000", margin: 0 }}>All Edges</h3>
                  {edgesMeta && (
                    <FixtureAccessPill
                      meta={edgesMeta}
                      fixtureId={fixtureIdNum}
                      variant="default"
                    />
                  )}
                </div>

                {edgesMeta && (
                  <p style={{ fontSize: 13, color: C.textSub, marginBottom: 12 }}>
                    {edgesMeta.hasFullAccess ? (
                      edgesMeta.isPremium ? (
                        <>
                          You're on <b>CSB Premium</b> – full model edges unlocked for this fixture.
                        </>
                      ) : (
                        <>
                          This fixture is unlocked. You've used <b>{edgesMeta.usedToday}</b> /{" "}
                          <b>{edgesMeta.limit}</b> free fixture unlocks today.
                        </>
                      )
                    ) : (
                      edgesMeta.authMismatch ? (
                        <>
                          You&apos;re signed in and your Premium status was detected, but this
                          edges request still came back as a free preview. Try refreshing this
                          page or signing out and back in on this device to refresh the session.
                        </>
                      ) : (
                        <>
                          Free preview only – showing a small sample of edges. You&apos;ve used{" "}
                          <b>{edgesMeta.usedToday}</b> / <b>{edgesMeta.limit}</b> free fixture unlocks today.{" "}
                          <Link to="/premium" style={{ color: "#FBBF24" }}>
                            Go Premium →
                          </Link>
                        </>
                      )
                    )}
                  </p>
                )}

                {uniqueBookmakers.length > 1 && (
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 13, color: C.textSub, marginRight: 8 }}>Filter by bookmaker: </label>
                    <select
                      value={selectedBookmaker}
                      onChange={(e) => setSelectedBookmaker(e.target.value)}
                      style={{
                        padding: "4px 8px",
                        borderRadius: 6,
                        border: `1px solid ${C.accentLight}`,
                        fontSize: 13,
                      }}
                    >
                      <option value="All">All</option>
                      {uniqueBookmakers.map((bm) => (
                        <option key={bm} value={bm}>
                          {bm}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 13, color: C.textSub, marginRight: 8 }}>Form window: </label>
                    <select
                      value={explainFormWindow}
                      onChange={(e) => setExplainFormWindow(Number(e.target.value))}
                      style={{
                        padding: "4px 8px",
                        borderRadius: 6,
                        border: `1px solid ${C.accentLight}`,
                        fontSize: 13,
                      }}
                    >
                      <option value={5}>Last 5</option>
                      <option value={10}>Last 10</option>
                      <option value={15}>Last 15</option>
                    </select>
                  </div>
                  {filteredEdges.map((e, i) => {
                    const key = `${e.market}-${e.bookmaker}-${i}`;
                    const cacheKey = getExplanationCacheKey(e.market, explainFormWindow);
                    const exp = explanations[cacheKey];
                    const expErr = explanationErrors[cacheKey];
                    const isExpLoading = explanationLoadingKey === cacheKey;
                    const rowWhy = getRowWhy(e);
                    const statPropMarket = isStatPropMarket(e.market);
                    const showGenericExplanation = !statPropMarket && !rowWhy;
                    const eg = showGenericExplanation ? exp?.form?.expected_goals : null;
                    const notes = getExplainNotes(exp, e);
                    const hasCupBadge = showGenericExplanation && hasCupContext(notes);

                    return (
                      <div
                        key={key}
                        style={{
                          padding: "12px",
                          borderRadius: 8,
                          background: C.greenLt,
                          border: `1px solid ${C.accentLight}`,
                          marginBottom: 10,
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                          <div style={{ color: "#000" }}>
                            <strong style={{ color: "#000" }}>{formatMarketName(e.market)}</strong> —{" "}
                            {e.bookmaker} @ <strong>{formatOddsBoth(e.price)}</strong>{" "}
                            {typeof e.edge === "number" && typeof e.prob === "number" ? (
                              <span style={{ color: "#000" }}>
                                ({(e.edge * 100).toFixed(1)}% edge, model p {(e.prob * 100).toFixed(1)}%)
                              </span>
                            ) : null}
                          </div>
                          <div style={{ display: "flex", gap: 8 }}>
                            <button
                              onClick={() => fetchExplanation(e.market, i, { edge: e })}
                              style={{
                                padding: "4px 10px",
                                borderRadius: 6,
                                border: `1px solid ${C.pitchLine}`,
                                background: C.cardBg,
                                color: "#000",
                                fontSize: 12,
                                cursor: "pointer",
                                fontWeight: 600,
                              }}
                            >
                              {isExpLoading && expandedWhy === i ? "Loading…" : "Why?"}
                            </button>
                          </div>
                        </div>

                        {expandedWhy === i && (
                          <EdgeExplanationCard
                            edge={e}
                            expectedGoals={eg}
                            notes={notes}
                            recommendation={showGenericExplanation ? exp?.recommendation : null}
                            formWindow={exp?.form_window || explainFormWindow}
                            showFormContext={showGenericExplanation}
                            cupContext={hasCupBadge}
                            loading={isExpLoading}
                            error={expErr}
                            onRetry={() => fetchExplanation(e.market, i, { force: true, edge: e })}
                            formatText={humanizeExplainText}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {activeTab === "Live Odds" && (
              <div
                style={{
                  background: C.cardBg,
                  borderRadius: 12,
                  padding: 20,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                }}
              >
                {liveLoading && <p style={{ padding: 16, color: C.textSub }}>Fetching live data…</p>}
                {liveError && <p style={{ padding: 16, color: C.red }}>{liveError}</p>}

                {!liveLoading && liveData && !liveData.in_play && (
                  <div style={{
                    padding: 24, borderRadius: 12, textAlign: "center",
                    background: "#f5f5f5", color: "#000",
                  }}>
                    <p style={{ fontSize: 18, fontWeight: 700, color: "#000", marginBottom: 8 }}>
                      Not in play
                    </p>
                    <p style={{ fontSize: 14, color: "#000" }}>
                      {liveData.status === "FT"
                        ? "This match has finished."
                        : liveData.status === "NS"
                          ? "This match hasn't started yet. Come back at kick-off to see live model prices."
                          : `Current status: ${liveData.status || "unknown"}`}
                    </p>
                    <button
                      style={{
                        marginTop: 12, padding: "6px 14px", borderRadius: 999,
                        border: "1px solid #ddd", background: "#fff",
                        color: "#000", cursor: "pointer", fontSize: 13,
                      }}
                      onClick={() => refreshLiveOdds("not-in-play")}
                    >
                      Refresh
                    </button>
                    {latestOddsSeen && (
                      <p style={{ marginTop: 10, fontSize: 12, color: "#000" }}>
                        Latest odds timestamp: {latestOddsSeen.toLocaleTimeString()}
                      </p>
                    )}
                    {!!liveRefreshNotice && (
                      <p style={{ marginTop: 8, fontSize: 12, color: "#000" }}>{liveRefreshNotice}</p>
                    )}
                  </div>
                )}

                {!liveLoading && liveData && liveData.in_play && (() => {
                  const { score, elapsed, model, status } = liveData;
                  const liveMarkets = ["HOME_WIN", "DRAW", "AWAY_WIN", "O2.5", "U2.5", "BTTS_Y", "BTTS_N"];
                  const MARKET_LABEL = {
                    HOME_WIN: fixture.home_team,
                    AWAY_WIN: fixture.away_team,
                  };

                  const bestByMarket = {};
                  for (const o of odds) {
                    const cur = bestByMarket[o.market];
                    const price = Number(o.price);
                    if (!cur || price > cur.price) {
                      bestByMarket[o.market] = { price, bookmaker: o.bookmaker };
                    }
                  }

                  return (
                    <div>
                      {/* Scoreboard */}
                      <div style={{
                        display: "flex", alignItems: "center", justifyContent: "center",
                        gap: 16, padding: "18px 0 12px", flexWrap: "wrap",
                      }}>
                        <div style={{ textAlign: "center", minWidth: 100 }}>
                          <div style={{ fontSize: 13, color: "#000", marginBottom: 4 }}>{fixture.home_team}</div>
                          <div style={{ fontSize: 36, fontWeight: 800, color: "#000" }}>{score.home}</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <span style={{
                            display: "inline-block", fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                            color: "#fff", background: "#16a34a", borderRadius: 999, padding: "2px 8px",
                          }}>LIVE</span>
                          <div style={{ fontSize: 13, color: "#000", marginTop: 4 }}>
                            {status === "HT" ? "Half Time" : `${elapsed}'`}
                          </div>
                        </div>
                        <div style={{ textAlign: "center", minWidth: 100 }}>
                          <div style={{ fontSize: 13, color: "#000", marginBottom: 4 }}>{fixture.away_team}</div>
                          <div style={{ fontSize: 36, fontWeight: 800, color: "#000" }}>{score.away}</div>
                        </div>
                      </div>

                      {/* Model fair prices vs bookmaker odds table */}
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8 }}>
                          <thead>
                            <tr>
                              <th style={{ textAlign: "left", padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.1)", fontSize: 13, color: "#000", background: "#fff" }}>Market</th>
                              <th style={{ textAlign: "right", padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.1)", fontSize: 13, color: "#000", background: "#fff" }}>Model p</th>
                              <th style={{ textAlign: "right", padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.1)", fontSize: 13, color: "#000", background: "#fff" }}>Fair Price</th>
                              <th style={{ textAlign: "right", padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.1)", fontSize: 13, color: "#000", background: "#fff" }}>Best Book</th>
                              <th style={{ textAlign: "right", padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.1)", fontSize: 13, color: "#000", background: "#fff" }}>Edge</th>
                            </tr>
                          </thead>
                          <tbody>
                            {liveMarkets.map((mkt) => {
                              const m = model[mkt];
                              if (!m) return null;
                              const best = bestByMarket[mkt];
                              const edge = best && m.prob > 0 ? (m.prob * best.price - 1) : null;
                              const hasEdge = edge !== null && edge > 0;

                              return (
                                <tr key={mkt} style={{ background: hasEdge ? "rgba(22,163,74,0.12)" : undefined }}>
                                  <td style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)", fontSize: 14, color: "#000" }}>
                                    {MARKET_LABEL[mkt] || formatMarketName(mkt)}
                                  </td>
                                  <td style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)", fontSize: 14, color: "#000", textAlign: "right" }}>
                                    {(m.prob * 100).toFixed(1)}%
                                  </td>
                                  <td style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)", fontSize: 14, color: "#000", textAlign: "right" }}>
                                    {m.fair_price?.toFixed(2) ?? "—"}
                                  </td>
                                  <td style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)", fontSize: 14, textAlign: "right" }}>
                                    {best ? (
                                      <span style={{ color: "#000" }}>
                                        {best.price.toFixed(2)} <span style={{ fontSize: 11, color: "#000" }}>({best.bookmaker})</span>
                                      </span>
                                    ) : <span style={{ color: "#000" }}>—</span>}
                                  </td>
                                  <td style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)", fontSize: 14, textAlign: "right", fontWeight: hasEdge ? 700 : 400, color: hasEdge ? "#16a34a" : "#000" }}>
                                    {edge !== null ? `${(edge * 100).toFixed(1)}%` : "—"}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Refresh + disclaimer */}
                      <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                        <button
                          style={{
                            padding: "6px 14px", borderRadius: 999,
                            border: "1px solid #ddd", background: "#fff",
                            color: "#000", cursor: "pointer", fontSize: 13,
                          }}
                          onClick={() => refreshLiveOdds("in-play")}
                        >
                          Refresh
                        </button>
                        <p style={{ fontSize: 11, color: "#000", margin: 0 }}>
                          Model prices update on refresh. Book prices are from the last odds poll.
                        </p>
                        {latestOddsSeen && (
                          <p style={{ fontSize: 11, color: "#000", margin: 0 }}>
                            Last odds seen: {latestOddsSeen.toLocaleTimeString()}
                          </p>
                        )}
                        {lastLiveRefreshAt && (
                          <p style={{ fontSize: 11, color: "#000", margin: 0 }}>
                            Refreshed: {lastLiveRefreshAt.toLocaleTimeString()}
                          </p>
                        )}
                        {!!liveRefreshNotice && (
                          <p style={{ fontSize: 11, color: "#000", margin: 0 }}>{liveRefreshNotice}</p>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {activeTab === "Odds" && (
              <div style={{ overflowX: "auto" }}>
                <OddsTable grouped={grouped} />
              </div>
            )}

            {activeTab === "Form" && (
              <div
                style={{
                  background: C.cardBg,
                  borderRadius: 12,
                  padding: 20,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                }}
              >
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
                  <label style={{ fontSize: 13, color: C.textSub }}>Form:</label>
                  <select
                    value={formN}
                    onChange={(e) => setFormN(Number(e.target.value))}
                    style={{
                      fontSize: 13,
                      padding: "4px 8px",
                      borderRadius: 6,
                      border: `1px solid ${C.accentLight}`,
                    }}
                  >
                    {[3, 5, 7, 10].map((v) => (
                      <option key={v} value={v}>
                        last {v}
                      </option>
                    ))}
                  </select>

                  <span
                    style={{
                      marginLeft: 8,
                      padding: "5px 9px",
                      borderRadius: 999,
                      background: C.greenLt,
                      color: C.accentDark,
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    All competitions
                  </span>
                </div>

                {formData ? (
                  <FormBreakdown
                    home={{ summary: formData.home.summary, recent: formData.home.recent }}
                    away={{ summary: formData.away.summary, recent: formData.away.recent }}
                    homeName={fixture.home_team}
                    awayName={fixture.away_team}
                    n={formData.n}
                  />
                ) : (
                  <p style={{ color: C.textSub, textAlign: "center", padding: 20 }}>Loading form data...</p>
                )}
              </div>
            )}

            {activeTab === "H2H" && (
              <div
                style={{
                  background: C.cardBg,
                  borderRadius: 12,
                  padding: 20,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                }}
              >
                {h2hLoading ? (
                  <p style={{ padding: 16, color: C.textSub }}>Loading H2H data...</p>
                ) : (
                  <H2HSection
                    data={h2hData}
                    homeName={fixture.home_team}
                    awayName={fixture.away_team}
                  />
                )}
              </div>
            )}

            {activeTab === "Table" && (
              <div
                style={{
                  background: C.cardBg,
                  borderRadius: 12,
                  padding: 20,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  overflowX: "auto",
                }}
              >
                {leagueTableLoading ? (
                  <p style={{ color: C.textSub, padding: 12 }}>Loading league table...</p>
                ) : leagueTableError ? (
                  <div style={{ padding: 12 }}>
                    <p style={{ color: C.red, margin: "0 0 8px 0" }}>{leagueTableError}</p>
                    <button
                      onClick={fetchLeagueTable}
                      style={{
                        padding: "6px 12px",
                        borderRadius: 8,
                        border: `1px solid ${C.accentLight}`,
                        background: C.cardBg,
                        color: C.textPri,
                        cursor: "pointer",
                      }}
                    >
                      Retry
                    </button>
                  </div>
                ) : !leagueTable.length ? (
                  <p style={{ color: C.textSub, padding: 12 }}>No table rows available for this league.</p>
                ) : (
                  <>
                  {leagueTableGroupName ? (
                    <div style={{ marginBottom: 12 }}>
                      <span style={{ color: "#a67c00", fontSize: 12, fontWeight: 800, letterSpacing: 0.8, textTransform: "uppercase" }}>
                        FIFA World Cup 2026
                      </span>
                      <h3 style={{ margin: "3px 0 0", color: "#092f1d", fontSize: 20 }}>{leagueTableGroupName}</h3>
                    </div>
                  ) : null}
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 680 }}>
                    <thead style={{ background: "#fff" }}>
                      <tr style={{ borderBottom: `2px solid ${C.pitchLine}`, background: "#fff" }}>
                        <th style={{ padding: "10px 8px", textAlign: "left", fontSize: 13, color: "#000", fontWeight: 600, background: "#fff" }}>#</th>
                        <th style={{ padding: "10px 8px", textAlign: "left", fontSize: 13, color: "#000", fontWeight: 600, background: "#fff" }}>Team</th>
                        <th style={{ padding: "10px 8px", textAlign: "center", fontSize: 13, color: "#000", fontWeight: 600, background: "#fff" }}>P</th>
                        <th style={{ padding: "10px 8px", textAlign: "center", fontSize: 13, color: "#000", fontWeight: 600, background: "#fff" }}>W</th>
                        <th style={{ padding: "10px 8px", textAlign: "center", fontSize: 13, color: "#000", fontWeight: 600, background: "#fff" }}>D</th>
                        <th style={{ padding: "10px 8px", textAlign: "center", fontSize: 13, color: "#000", fontWeight: 600, background: "#fff" }}>L</th>
                        <th style={{ padding: "10px 8px", textAlign: "center", fontSize: 13, color: "#000", fontWeight: 600, background: "#fff" }}>GF</th>
                        <th style={{ padding: "10px 8px", textAlign: "center", fontSize: 13, color: "#000", fontWeight: 600, background: "#fff" }}>GA</th>
                        <th style={{ padding: "10px 8px", textAlign: "center", fontSize: 13, color: "#000", fontWeight: 600, background: "#fff" }}>GD</th>
                        <th style={{ padding: "10px 8px", textAlign: "center", fontSize: 13, color: "#000", fontWeight: 600, background: "#fff" }}>Pts</th>
                        <th style={{ padding: "10px 8px", textAlign: "center", fontSize: 13, color: "#000", fontWeight: 600, background: "#fff" }}>Form</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leagueTable.map((row, i) => {
                        const isHome =
                          (row?.team_id && String(row.team_id) === String(firstDefined(fixture.home_team_id, fixture.provider_home_team_id))) ||
                          normalizeWorldCupTeamName(row?.team) === normalizeWorldCupTeamName(fixture.home_team);
                        const isAway =
                          (row?.team_id && String(row.team_id) === String(firstDefined(fixture.away_team_id, fixture.provider_away_team_id))) ||
                          normalizeWorldCupTeamName(row?.team) === normalizeWorldCupTeamName(fixture.away_team);
                        const bgColor = isHome || isAway ? C.greenLt : undefined;
                        const displayStat = (value) =>
                          leagueTableGroupName && (value === null || value === undefined || value === "")
                            ? "-"
                            : toNum(value);
                        const gf = displayStat(row?.gf);
                        const ga = displayStat(row?.ga);
                        const gd =
                          gf === "-" || ga === "-"
                            ? "-"
                            : firstDefined(row?.goal_difference, Number(gf) - Number(ga));

                        return (
                          <tr key={`${row?.team || "team"}-${i}`} style={{ borderBottom: `1px solid ${C.accentLight}`, background: bgColor }}>
                            <td style={{ padding: "10px 8px", color: "#000" }}>{firstDefined(row?.position, i + 1)}</td>
                            <td style={{ padding: "10px 8px", color: "#000" }}>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                              {row?.team_logo ? (
                                <img
                                  src={row.team_logo}
                                  alt=""
                                  width={24}
                                  height={24}
                                  style={{ objectFit: "contain" }}
                                  onError={(event) => {
                                    event.currentTarget.style.display = "none";
                                  }}
                                />
                              ) : null}
                              {row?.team_id ? (
                                <Link
                                  to={`/club/${row.team_id}/${slugifyTeamName(row?.team || "club")}`}
                                  style={{ textDecoration: "none", color: "#000" }}
                                >
                                  {row?.team || "Unknown"}
                                </Link>
                              ) : (
                                <span>{row?.team || "Unknown"}</span>
                              )}
                              </span>
                            </td>
                            <td style={{ padding: "10px 8px", textAlign: "center", color: "#000" }}>{displayStat(row?.played)}</td>
                            <td style={{ padding: "10px 8px", textAlign: "center", color: "#000" }}>{displayStat(row?.win)}</td>
                            <td style={{ padding: "10px 8px", textAlign: "center", color: "#000" }}>{displayStat(row?.draw)}</td>
                            <td style={{ padding: "10px 8px", textAlign: "center", color: "#000" }}>{displayStat(row?.lose)}</td>
                            <td style={{ padding: "10px 8px", textAlign: "center", color: "#000" }}>{gf}</td>
                            <td style={{ padding: "10px 8px", textAlign: "center", color: "#000" }}>{ga}</td>
                            <td style={{ padding: "10px 8px", textAlign: "center", color: "#000" }}>{gd}</td>
                            <td style={{ padding: "10px 8px", textAlign: "center", color: "#000" }}>{displayStat(row?.points)}</td>
                            <td style={{ padding: "10px 8px", textAlign: "center", color: "#000" }}>{row?.form || "-"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </>
                )}
              </div>
            )}

            {activeTab === "Predictions" && <PredictionsSection fixtureId={id} />}

            {activeTab === "Injuries" && <InjuriesSection fixtureId={id} />}
            {activeTab === "Props" && (
              <PlayerPropsSection fixtureId={id} homeTeam={fixture.home_team} awayTeam={fixture.away_team} />
            )}
            {activeTab === "Insights" && (
              <MatchInsights fixtureId={fixtureIdNum} fixture={fixture} insights={insights} loading={insightsLoading} limit={4} />
            )}
            {activeTab === "Community" && (
              <>
                <Poll fixtureId={id} homeTeam={fixture.home_team} awayTeam={fixture.away_team} />
                <MediaWatchSection
                  mode="fixture"
                  fixtureId={fixtureIdNum}
                  title="Media Watch"
                  subtitle="Trusted and official posts matched to this fixture."
                  limit={8}
                  sinceDays={14}
                  dark={false}
                  emptyText="No major media posts matched to this fixture yet."
                  hideWhenEmpty={false}
                />

                <section
                  style={{
                    margin: "18px 0",
                    padding: 14,
                    border: "1px solid rgba(15,23,42,0.1)",
                    borderRadius: 8,
                    background: "#fff",
                    color: "#111827",
                  }}
                >
                  <h2 style={{ margin: "0 0 4px", fontSize: 20 }}>Fixture Conversation</h2>
                  <div style={{ color: "rgba(17,24,39,0.62)", fontSize: 13, marginBottom: 10 }}>
                    Like the match thread or leave a quick comment.
                  </div>
                  <SocialEngagement targetType="fixture" targetId={fixtureIdNum} dark={false} />
                </section>

                <FixtureSocialPosts
                  fixtureId={fixtureIdNum}
                  title="Fan Views"
                  limit={8}
                  dark={false}
                />
                <TeamContextPanel fixtureId={fixtureIdNum} homeTeam={fixture.home_team} awayTeam={fixture.away_team} />
                <LeagueFixtures fixtures={leagueFixtures} />
              </>
            )}

            {activeTab === "Players" && (
              <PlayersSection
                fixtureId={id}
                homeTeam={fixture.home_team}
                awayTeam={fixture.away_team}
              />
            )}

            {activeTab === "Events" && (
              <EventsSection
                fixtureId={id}
                initialEvents={fixtureEvents}
                homeTeamId={homeTeamId}
                awayTeamId={awayTeamId}
              />
            )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
